import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

/**
 * TenantContext — Provides tenant branding to all components.
 * 
 * On mount, fetches branding from /api/auth/tenant (if not already
 * provided from the login response). Stores tenant data in state
 * and localStorage for persistence across page reloads.
 * 
 * Usage:
 *   const { tenant } = useTenant();
 *   <h1>{tenant.name}</h1>
 *   <p>{tenant.branding.tagline}</p>
 */

const TenantContext = createContext({
        tenant: {
                name: 'TheRealConnect.cloud',
                slug: '',
                company: '',
                branding: {
                        primary_color: '#10b981',
                        secondary_color: '#059669',
                        logo_url: null,
                        tagline: '',
                }
        },
        setTenant: () => { },
});

export const TenantProvider = ({ children, apiBase }) => {
        const [tenant, setTenantState] = useState(() => {
                // Try to restore from localStorage
                const saved = localStorage.getItem('tenant');
                if (saved) {
                        try { return JSON.parse(saved); } catch (e) { /* ignore */ }
                }
                return {
                        name: 'Loading...',
                        slug: '',
                        company: '',
                        branding: {
                                primary_color: '#10b981',
                                secondary_color: '#059669',
                                logo_url: null,
                                tagline: '',
                        }
                };
        });

        const setTenant = (data) => {
                setTenantState(data);
                localStorage.setItem('tenant', JSON.stringify(data));
        };

        // Fetch tenant branding on mount
        useEffect(() => {
                const fetchTenant = async () => {
                        try {
                                const res = await axios.get(`${apiBase}/auth/tenant`);
                                if (res.data && res.data.name) {
                                        setTenant(res.data);
                                }
                        } catch (err) {
                                console.warn('Could not fetch tenant branding:', err.message);
                        }
                };
                fetchTenant();
                // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [apiBase]);

        return (
                <TenantContext.Provider value={{ tenant, setTenant }}>
                        {children}
                </TenantContext.Provider>
        );
};

export const useTenant = () => useContext(TenantContext);

export default TenantContext;
